// index.tsx
import React, { useEffect, useState } from "react";
import { Table } from "antd";
import { DatatableProps } from "../../data/interface"; // Ensure correct path


const Datatable: React.FC<DatatableProps> = ({ columns, dataSource, Selection, selectedRowKeys: propSelectedRowKeys,
  setSelectedRowKeys: propSetSelectedRowKeys, onRow, isShowSearch = true, pagination = true, onChange, onSearch, serverSideSearch, setSearchValue, searchValue }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>(propSelectedRowKeys || []);
  const [searchText, setSearchText] = useState<string>(searchValue || "");
  const [Selections, setSelections] = useState<any>(true);
  const [filteredDataSource, setFilteredDataSource] = useState(dataSource);

  console.log('dataSource', dataSource)
  // Always update filteredDataSource when dataSource changes
  useEffect(() => {
    setFilteredDataSource(dataSource);
  }, [dataSource]);

  // Sync internal state when controlled value changes (server-side mode)
  useEffect(() => {
    if (serverSideSearch) {
      setSearchText(searchValue || "");
    }
  }, [serverSideSearch, searchValue]);

  // Keep latest onSearch in a ref to avoid effect retriggers on new function identity
  const onSearchRef = React.useRef<typeof onSearch>(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Store debounce timer id so we can cancel on blur
  const searchTimeoutRef = React.useRef<number | undefined>(undefined);

  // Debounce for server-side search
  useEffect(() => {
    if (!serverSideSearch || !onSearchRef.current) return;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const id = window.setTimeout(() => {
      onSearchRef.current && onSearchRef.current(searchText);
    }, 400);
    searchTimeoutRef.current = id;
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = undefined;
      }
    };
  }, [searchText, serverSideSearch]);

  const handleSearch = (value: string) => {
    if (serverSideSearch) {
      setSearchText(value);
      setSearchValue && setSearchValue(value);
      return;
    }
    setSearchText(value);
    const filteredData = dataSource.filter((record) =>
      Object.values(record).some((field) =>
        String(field).toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredDataSource(filteredData);
  };

  // Use controlled selectedRowKeys if provided
  const rowSelection = {
    selectedRowKeys: propSelectedRowKeys ?? selectedRowKeys,
    onChange: propSetSelectedRowKeys ?? setSelectedRowKeys,
  };

  // Check if row selection is needed - hide when Selection is false
  const shouldShowRowSelection = Selection !== false && (propSelectedRowKeys !== undefined || propSetSelectedRowKeys !== undefined);

  useEffect(() => {
    setSelections(Selection);
  }, [Selection])

  useEffect(() => {
    const withKeys = dataSource.map((item: any, index: number) => ({
      ...item,
      key: item.id ?? index, // Make sure `key` is present
    }));
    setFilteredDataSource(withKeys);
  }, [dataSource]);

  const sharedPagination = pagination ? {
    locale: { items_per_page: "" },
    nextIcon: <span>Next</span>,
    prevIcon: <span>Prev</span>,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30"],
  } : false as const;

  return (
    <>
      <div className="table-top-data d-flex px-3 justify-content-between">
        <div className="page-range">
        </div>
        <div className="serch-global text-right">
          {isShowSearch ? (
            <input
              type="search"
              className="form-control form-control-sm mb-3 w-auto float-end"
              value={serverSideSearch ? (searchValue ?? searchText) : searchText}
              placeholder="Search"
              onChange={(e) => handleSearch(e.target.value)}
              onBlur={() => {
                if (searchTimeoutRef.current) {
                  clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = undefined;
                }
              }}
              aria-controls="DataTables_Table_0"
            />
          ) : (
            <div className="form-control form-control-sm mb-3 w-auto float-end" style={{ visibility: 'hidden', minHeight: '32px' }}>
              <span style={{ visibility: 'hidden' }}>Search</span>
            </div>
          )}
        </div>
      </div>
      {!Selections ?
        <Table
          className="table datanew dataTable no-footer"
          rowSelection={shouldShowRowSelection ? rowSelection : undefined}
          columns={columns}
          rowHoverable={false}
          dataSource={filteredDataSource}
          onRow={onRow}
          onChange={onChange}
          pagination={sharedPagination}
        /> :
        <Table
          className="table datanew dataTable no-footer"
          rowSelection={shouldShowRowSelection ? rowSelection : undefined}
          columns={columns}
          rowHoverable={true}
          dataSource={filteredDataSource}
          onRow={(record) => {
            const customRowProps = onRow ? onRow(record) : {};
            return {
              ...customRowProps,
              onClick: (event: React.MouseEvent) => {
                // Call custom onClick if it exists
                if (customRowProps.onClick) {
                  customRowProps.onClick(event);
                }

                // Only proceed with selection if event wasn't stopped
                if (!event.defaultPrevented) {
                  const key = record.key ?? record.id;
                  const currentKeys = propSelectedRowKeys ?? selectedRowKeys;
                  const setKeys = propSetSelectedRowKeys ?? setSelectedRowKeys;

                  const newKeys = currentKeys.includes(key)
                    ? currentKeys.filter((k) => k !== key)
                    : [...currentKeys, key];

                  setKeys(newKeys);
                }
              }
            };
          }}
          pagination={sharedPagination}
          onChange={onChange}
        />
      }
    </>
  );
};

export default Datatable;